import { classes, events } from '@theme/constants';
import { getRefs, parseHtml } from '@theme/dom';
import { publish } from '@theme/event-bus';
import { sectionStatusHandler, text } from '@theme/fetch';
import { debounce, isFromBreakpoint, throttle, updateQueryParam } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

/**
 * Handles filter and sort selections with the Section Rendering API.
 */
class ResultsList extends HTMLCustomElement {
  loading = false;

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('change', this.refs.sort, this.handleFormSubmit.bind(this));
    this.on('change', this.refs.filters, this.handleFormSubmit.bind(this));

    this.on(
      'scroll',
      window,
      throttle(() => {
        const { bottom } = this.refs.results.getBoundingClientRect();
        const isBottomVisible = bottom < window.innerHeight && bottom;

        if (!this.loading && isBottomVisible) {
          this.loadNextPage();
        }
      }),
    );

    this.subscribe(events.toggleElementOpening, ({ id }) => {
      if (id === 'filter-form') {
        requestAnimationFrame(() => {
          this.refs.filterFormContainer.style.height = `calc(100dvh - var(--toolbar-height) - ${this.refs.filterFormContainer.getBoundingClientRect().top}px)`;
        });
      }
    });

    this.on(
      'resize',
      window,
      debounce(() => {
        if (this.refs.filterToggleElement.classList.contains(classes.open) && isFromBreakpoint('medium')) {
          this.refs.filterToggleElement.close();
          this.refs.filterFormContainer.style.height = '';
        }
      }),
    );
  }

  /**
   * Handles the sort or filter form submit event.
   * @param {SubmitEvent} event - The form submit event.
   */
  async handleFormSubmit(event) {
    event.preventDefault();

    const data = [...new FormData(this.refs.filters), ...new FormData(this.refs.sort)];
    const params = this.filterSearchParams(new URLSearchParams(data));
    const section = this.sectionId ? ['section_id', this.sectionId] : [null, null];
    const query = new URLSearchParams([...params.entries(), section]).toString();
    const { origin, pathname } = window.location;

    /**
     * Update the query parameters.
     */
    window.history.pushState({}, '', `${origin}${pathname}${params.size === 0 ? '' : `?${params.toString()}`}`);

    try {
      await text(fetch(`${origin}${pathname}?${query}`), sectionStatusHandler).then(async (response) => {
        if (this.sectionId) {
          publish(events.sectionUpdate, {
            sections: {
              [this.sectionId]: response,
            },
          });
        }
      });
    } catch {
      /**
       * Since we couldn't update the results, reload the page.
       */
      window.location.reload();
    }
  }

  /**
   * Loads the next page.
   */
  loadNextPage() {
    const { next } = getRefs(this);

    if (!next) return;

    this.loading = true;

    const url = new URL(next.href);
    const page = url.searchParams.get('page') ?? 1;

    if (this.sectionId) {
      url.searchParams.append('section_id', this.sectionId);
    }

    text(fetch(url.href)).then((response) => {
      const { list, next: nextEl } = getRefs(parseHtml(response, this.tagName.toLowerCase()));

      this.refs.list.insertAdjacentHTML('beforeend', list.innerHTML);

      updateQueryParam('page', page === 1 ? null : page);

      if (nextEl) {
        next.href = nextEl.href;
      } else {
        next.remove();
      }

      this.loading = false;
    });
  }

  /**
   * Filters out empty values, or the default sort by option.
   * @param {URLSearchParams} params - The search params.
   * @returns {URLSearchParams}
   */
  filterSearchParams(params) {
    return new URLSearchParams([
      ...params.entries().filter(([key, value]) => {
        if (value === '') {
          return false;
        }

        if (key === 'sort_by' && this.hasAttribute('default-sort-by')) {
          return !(value === this.getAttribute('default-sort-by'));
        }

        return true;
      }),
    ]);
  }

  /**
   * The section container.
   */
  get sectionId() {
    return this.getAttribute('section-id');
  }
}

if (!customElements.get('results-list')) customElements.define('results-list', ResultsList);
